<template>
  <div class="tech-events-page">
    <!-- Header Section -->
    <div class="content__page-header">
      <h1>{{ $t('events.title') }}</h1>
      <p>{{ $t('events.description') }}</p>
    </div>

    <!-- Filters Section -->
    <div class="filters-section">
      <el-row :gutter="20">
        <el-col :xs="24" :sm="12" :md="8">
          <el-input
            v-model="searchQuery"
            :placeholder="$t('events.search_placeholder')"
            clearable
            class="search-input"
          >
            <template #prefix>
              <i class="el-icon-search"></i>
            </template>
          </el-input>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8">
          <el-select 
            v-model="selectedCountry" 
            :placeholder="$t('events.filter_by_country')" 
            clearable
            class="country-select"
          >
            <el-option
              v-for="country in countries"
              :key="country.code"
              :label="$t(`countries.${country.code}`, country.code)"
              :value="country.code"
            />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="24" :md="8" class="filter-actions">
          <el-button 
            @click="resetFilters" 
            type="default" 
            size="medium"
            class="clear-filters-btn"
          >
            {{ $t('events.clear_filters') }}
          </el-button>
        </el-col>
      </el-row>
    </div>

    <!-- Events Grid -->
    <div class="events-grid" v-loading="loading">
      <template v-if="activeEvents.length > 0">
        <h1 class="section-title">{{ $t('events.featured_events') }}</h1>
        <el-row :gutter="20">
          <el-col
            v-for="event in activeEvents"
            :key="event.id"
            :xs="24"
            :sm="12"
            :md="8"
            :lg="6"
          >
            <el-card class="event-card" shadow="hover">
              <div class="event-image" :style="{ backgroundImage: `url(${event.image_url || '/images/event-placeholder.png'})` }">
                <div class="date-badge">
                  <span class="month">{{ formatMonth(event.start_date) }}</span>
                  <span class="day">{{ formatDay(event.start_date) }}</span>
                </div>
              </div>
              <div class="event-content">
                <h3 class="event-title">{{ event.name }}</h3>
                <div class="event-meta">
                  <el-tag size="medium" type="info" effect="light">
                    <i class="el-icon-location"></i>
                    {{ event.city }}, {{ $t(`countries.${event.country_code}`, event.country_code) }}
                  </el-tag>
                </div>
                <div class="event-description" v-if="event.description">
                  {{ truncateDescription(event.description) }}
                </div>
                <div class="event-actions">
                  <el-button 
                    v-if="event.website"
                    class="learn-more-btn"
                    @click="openEventWebsite(event.website)"
                  >
                    {{ $t('events.learn_more') }}
                  </el-button>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </template>

      <template v-if="inactiveEvents.length > 0">
        <h1 class="section-title">{{ $t('events.more_events') }}</h1>
        <div class="table-container">
          <table class="events-table">
            <thead>
              <tr>
                <th>{{ $t('events.name') }}</th>
                <th>{{ $t('events.location') }}</th>
                <th>{{ $t('events.dates') }}</th>
                <th>{{ $t('events.website') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in inactiveEvents" :key="event.id" class="table-row">
                <td>{{ event.name }}</td>
                <td>{{ event.city }}, {{ $t(`countries.${event.country_code}`, event.country_code) }}</td>
                <td>
                  {{ formatDate(event.start_date) }}
                  <span v-if="event.end_date"> - {{ formatDate(event.end_date) }}</span>
                </td>
                <td>
                  <a
                    v-if="event.website"
                    :href="event.website"
                    target="_blank"
                    class="inactive-website"
                  >
                    {{ event.website }}
                  </a>
                  <span v-else>{{ $t('events.unavailable') }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <el-empty
        v-if="!loading && filteredEvents.length === 0"
        :image-size="120"
        :description="$t('events.no_results')"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import apolloClient from '@/services/apollo-client';
import { GET_TECH_EVENTS } from '@/graphql/tech-events';
import { useI18n } from 'vue-i18n';

export default {
  name: 'TechEventsPage',
  setup() {
    const events = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const selectedCountry = ref('');
    const { t, locale } = useI18n();
    const countries = ref([]);

    const fetchEvents = async () => {
      loading.value = true;
      try {
        const { data } = await apolloClient.query({
          query: GET_TECH_EVENTS,
          variables: { upcoming: true },
        });
        events.value = data.techEvents;
        countries.value = data.countries;
      } catch (err) {
        error.value = err;
        console.error('Error fetching events:', err);
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString(locale.value, {
        month: 'short',
        day: 'numeric'
      });
    };

    const formatMonth = (dateStr) => {
      if (!dateStr) return '';
      return new Intl.DateTimeFormat(locale.value, {
        month: 'short',
      }).format(new Date(dateStr)).toUpperCase();
    };

    const formatDay = (dateStr) => {
      if (!dateStr) return '';
      return new Intl.DateTimeFormat(locale.value, {
        day: 'numeric',
      }).format(new Date(dateStr));
    };

    const truncateDescription = (description) => {
      if (!description) return '';
      const maxLength = 200;
      return description.length > maxLength
        ? description.substring(0, maxLength) + '...'
        : description;
    };

    const openEventWebsite = (url) => {
      window.open(url, '_blank');
    };

    const openGoogleMaps = (url) => {
      window.open(url, '_blank');
    };

    const filteredEvents = computed(() => {
      return events.value.filter(event => {
        const matchesSearch =
          !searchQuery.value ||
          [event.name, event.city, event.description].some(field =>
            field && typeof field === 'string' && field.toLowerCase().includes(searchQuery.value.toLowerCase())
          );

        const matchesCountry = !selectedCountry.value || event.country_code === selectedCountry.value;

        return matchesSearch && matchesCountry;
      });
    });

    const activeEvents = computed(() => {
      return filteredEvents.value.filter(event => event.website_active);
    });

    const inactiveEvents = computed(() => {
      return filteredEvents.value.filter(event => !event.website_active);
    });

    const resetFilters = () => {
      searchQuery.value = '';
      selectedCountry.value = '';
    };

    const countryName = (code) => {
      return t(`countries.${code}`, code);
    };

    onMounted(fetchEvents);

    return {
      events,
      filteredEvents,
      countries,
      searchQuery,
      selectedCountry,
      loading,
      resetFilters,
      formatDate,
      formatMonth,
      formatDay,
      truncateDescription,
      openEventWebsite,
      openGoogleMaps,
      error,
      activeEvents,
      inactiveEvents,
      countryName,
    };
  },
};
</script>

<style scoped>
/* .tech-events-page {
  min-height: 100vh;
  background-color: #f5f7fa;
} */

/* .content__page-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 20px;
} */

.filters-section {
  background: white;
  padding: 20px;
  margin-bottom: 30px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.search-input,
.country-select {
  width: 100%;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.events-grid {
  padding: 20px;
}

.event-card {
  margin-bottom: 20px;
  transition: transform 0.3s ease;
  border: none;
  border-radius: 8px;
  overflow: hidden;
  height: 500px;
  display: flex;
  flex-direction: column;
}

.event-card:hover {
  transform: translateY(-5px);
}

.event-image {
  height: 200px;
  background-size: cover;
  background-position: center;
  position: relative;
  background-color: #f0f2f5;
}

.date-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  background: white;
  padding: 8px;
  border-radius: 4px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.date-badge .month {
  display: block;
  font-size: 12px;
  color: #409EFF;
  font-weight: bold;
}

.date-badge .day {
  display: block;
  font-size: 20px;
  font-weight: bold;
  color: #303133;
}

.event-content {
  padding: 15px 20px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.event-title {
  font-size: 1.8rem;
  margin: 5px 0 12px;
  color: #303133;
  font-weight: 600;
  line-height: 1.3;
}

.event-meta {
  margin-bottom: 15px;
}

.event-meta .el-tag {
  font-size: 1rem;
  padding: 8px 12px;
}

.event-meta .el-tag i {
  margin-right: 6px;
}

.event-description {
  color: #606266;
  margin-bottom: 20px;
  line-height: 1.5;
  flex: 1;
  overflow: hidden;
  font-size: 1.4rem;
}

.event-actions {
  display: flex;
  gap: 10px;
  margin-top: auto;
}

.learn-more-btn,
.view-map-btn {
  flex: 1;
}

@media (max-width: 768px) {
  .filter-actions {
    justify-content: center;
    margin-top: 20px;
  }

  .event-actions {
    flex-direction: column;
  }
}

.section-title {
  font-size: 2rem;
  color: #303133;
  margin: 40px 0 20px;
  font-weight: 600;
}

.events-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

.events-table th,
.events-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ebeef5;
}

.events-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #606266;
}

.table-row {
  transition: background-color 0.3s ease;
}

.table-row:hover {
  background-color: #f5f7fa;
}

.inactive-website {
  color: #909399;
  text-decoration: line-through;
  pointer-events: none;
  cursor: not-allowed;
}
</style>
